import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { leadsApi, type LeadTypeKey, type LeadUpdate } from './api';
import { getDetailFields } from './fields';
import { CallRequestPanel } from './CallRequestPanel';
import { LeadSourcePanel } from './LeadSource';
import { LeadEstimatePanel } from './LeadEstimate';
import { mediaApi } from '@/features/media/api';
import { keys } from '@/lib/query/keys';
import { useApiError } from '@/lib/query/useApiError';
import { useGate } from '@/lib/rbac/Gate';
import type { LeadTypeConfig } from '@/config/leadTypes';
import type { CallChannel, CallWindow, LeadStatus, NewsletterStatus, User } from '@/lib/api/types';
import { Dialog } from '@/lib/ui/Dialog';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import { Button, PermissionButton } from '@/lib/ui/Button';
import { Select, type SelectOption } from '@/lib/ui/Select';
import { TextArea } from '@/lib/ui/Field';
import { MonoValue, Eyebrow } from '@/lib/ui/Typography';
import { LeadStatusPill, NewsletterStatusPill } from '@/lib/ui/StatusPill';
import { useToast } from '@/lib/ui';
import { formatBytes, formatDateTime } from '@/lib/format';

type Row = Record<string, unknown>;

interface Props {
  type: LeadTypeKey;
  cfg: LeadTypeConfig;
  row: Row;
  users: User[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Read a string-ish field off a heterogeneous lead row. */
function str(row: Row, key: string): string {
  const v = row[key];
  return v == null ? '' : String(v);
}

/** Detail + manage panel for a single lead (spec §11.2–§11.5). */
export function LeadDetailDialog({ type, cfg, row, users, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const onError = useApiError();
  const { allowed: canManage } = useGate('leads:manage');

  const rowId = str(row, 'id');

  // Fetch the freshest record for the detail panel (notes can be stale in the list).
  const detailQuery = useQuery({
    queryKey: keys.leads.detail(type, rowId),
    queryFn: () => leadsApi.get(type, rowId),
    enabled: open && cfg.hasDetail,
  });
  const record: Row = (detailQuery.data as Row | undefined) ?? row;

  // Manage panel local state — seeded from the loaded record (notes REPLACES, so load current).
  const [status, setStatus] = useState<string>(str(record, 'status'));
  const [assignedTo, setAssignedTo] = useState<string>(str(record, 'assigned_to'));
  const [notes, setNotes] = useState<string>(str(record, 'notes'));
  const [confirmErase, setConfirmErase] = useState(false);

  const recordStatus = str(record, 'status');
  const recordAssigned = str(record, 'assigned_to');
  const recordNotes = str(record, 'notes');
  useEffect(() => {
    setStatus(recordStatus);
    setAssignedTo(recordAssigned);
    setNotes(recordNotes);
  }, [rowId, recordStatus, recordAssigned, recordNotes]);

  const save = useMutation({
    mutationFn: () => {
      const body: LeadUpdate = {
        status: (status || undefined) as LeadStatus | undefined,
        assigned_to: assignedTo || null,
        notes,
      };
      return leadsApi.update(type, rowId, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.leads.all(type) });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      toast.success(t('leads.leadUpdated'));
    },
    onError,
  });

  const erase = useMutation({
    mutationFn: () => leadsApi.erase(type, rowId),
    onSuccess: () => {
      // The record is gone — invalidate only the lists, NOT keys.leads.all (which is a
      // prefix of the still-mounted detail query and would refetch the deleted id → 404).
      // Closing the dialog disables the detail query; its cache is left to be GC'd.
      void qc.invalidateQueries({ queryKey: keys.leads.lists(type) });
      void qc.invalidateQueries({ queryKey: keys.dashboard() });
      toast.success(t('leads.recordErased'));
      setConfirmErase(false);
      onOpenChange(false);
    },
    onError,
  });

  const statusOptions: SelectOption[] = cfg.statuses.map((s) => ({
    value: s,
    label: t(`leadStatus.${s}`),
  }));
  const assigneeOptions: SelectOption[] = [
    { value: '', label: t('leads.unassigned') },
    ...users.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` })),
  ];

  const fields = getDetailFields(type);
  const fullName = str(record, 'full_name');
  const email = str(record, 'email');
  const assigneeName = users.find((u) => u.id === recordAssigned)?.name;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={fullName || email || t('leads.leadDetail')}
      description={email && fullName ? email : undefined}
    >
      <div className="flex flex-col gap-lg">
        {/* Status + assignee summary */}
        <div className="flex flex-wrap items-center gap-sm">
          {type === 'newsletter' ? (
            <NewsletterStatusPill status={recordStatus as NewsletterStatus} />
          ) : (
            recordStatus && <LeadStatusPill status={recordStatus as LeadStatus} />
          )}
          {assigneeName && (
            <span className="text-small text-ink-mute">
              {t('leads.assignedTo', { name: assigneeName })}
            </span>
          )}
          <span className="text-small text-ink-mute">
            {t('leads.createdAt', { date: formatDateTime(str(record, 'created_at')) })}
          </span>
        </div>

        {/* Call request: channel + preferred windows, ahead of the generic fields */}
        {type === 'contact' && str(record, 'request_kind') === 'call' && (
          <CallRequestPanel
            channel={str(record, 'call_channel') as CallChannel}
            windows={
              (Array.isArray(record.call_windows) ? record.call_windows : []) as CallWindow[]
            }
            visitorZone={str(record, 'call_timezone')}
            phone={str(record, 'phone')}
          />
        )}

        {/* Captured fields */}
        <dl className="grid grid-cols-1 gap-md sm:grid-cols-2">
          {fields.map((f) => {
            const v = record[f.key];
            if (v == null || v === '') return null;
            let rendered: ReactNode;
            if (f.kind === 'bool') rendered = v ? t('leads.yes') : t('leads.no');
            else if (f.kind === 'mono') rendered = <MonoValue>{String(v)}</MonoValue>;
            else rendered = String(v);
            return (
              <div key={f.key}>
                <dt className="eyebrow text-ink-mute">{t(f.labelKey)}</dt>
                <dd className="mt-2xs text-small text-ink-graphite whitespace-pre-wrap">
                  {rendered}
                </dd>
              </div>
            );
          })}
        </dl>

        {/* Application CV (restricted asset, fresh presigned URL on demand, never cached) */}
        {type === 'applications' && str(record, 'resume_media_id') && (
          <ResumeLink mediaId={str(record, 'resume_media_id')} />
        )}

        {/* Contact attachment metadata only (no inline download in v1) */}
        {type === 'contact' && str(record, 'attachment_id') && (
          <div className="rounded-md border-[0.5px] border-hairline bg-canvas-bone p-md">
            <Eyebrow>{t('leads.attachment')}</Eyebrow>
            <p className="mt-2xs text-small text-ink-graphite">
              {str(record, 'attachment_name') || t('leads.file')}
              {typeof record.attachment_size === 'number' && record.attachment_size > 0 ? (
                <>
                  {' · '}
                  <MonoValue>{formatBytes(record.attachment_size)}</MonoValue>
                </>
              ) : null}
            </p>
            <p className="mt-2xs text-small text-ink-mute">{t('leads.metadataOnly')}</p>
          </div>
        )}

        {/* The /pricing estimate, as the server priced it on the published card */}
        {type === 'contact' && <LeadEstimatePanel meta={record.meta} />}

        {/* Attribution: channel, campaign, click ids, landing path, referrer (never the IP) */}
        <LeadSourcePanel source={record.source} meta={record.meta} />

        {/* Manage panel (gated, only if updatable) */}
        {cfg.updatable && (
          <div className="flex flex-col gap-md border-t-[0.5px] border-hairline pt-lg">
            <Eyebrow>{t('leads.manage')}</Eyebrow>
            <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
              <Select
                label={t('common.status')}
                value={status || undefined}
                onValueChange={setStatus}
                options={statusOptions}
                disabled={!canManage}
              />
              <Select
                label={t('leads.assignedToLabel')}
                value={assignedTo}
                onValueChange={setAssignedTo}
                options={assigneeOptions}
                disabled={!canManage}
              />
            </div>
            <TextArea
              label={t('leads.internalNotes')}
              hint={t('leads.notesReplaceHint')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!canManage}
            />
            <div className="flex justify-end">
              <PermissionButton
                allowed={canManage}
                reason={t('leads.managePermissionReason')}
                loading={save.isPending}
                onClick={() => save.mutate(undefined)}
              >
                {t('leads.saveChanges')}
              </PermissionButton>
            </div>
          </div>
        )}

        {/* PDPL erasure (gated, only if hasErasure) */}
        {cfg.hasErasure && (
          <div className="flex flex-col gap-sm border-t-[0.5px] border-hairline pt-lg">
            <Eyebrow className="text-signal-error">{t('leads.dangerZone')}</Eyebrow>
            <p className="text-small text-ink-mid">{t('leads.erasureDescription')}</p>
            <div className="flex justify-start">
              <PermissionButton
                allowed={canManage}
                reason={t('leads.managePermissionReason')}
                variant="danger"
                onClick={() => setConfirmErase(true)}
              >
                {t('leads.erasePermanently')}
              </PermissionButton>
            </div>
            <ConfirmDialog
              open={confirmErase}
              onOpenChange={setConfirmErase}
              tone="danger"
              title={t('leads.confirmEraseTitle')}
              description={t('leads.confirmEraseDescription')}
              confirmPhrase={email || 'ERASE'}
              confirmLabel={t('leads.erasePermanently')}
              loading={erase.isPending}
              onConfirm={() => erase.mutate(undefined)}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}

/** Fetches a fresh presigned URL for the restricted CV asset on demand. Never cached. */
function ResumeLink({ mediaId }: { mediaId: string }) {
  const { t } = useTranslation();
  const toast = useToast();
  const onError = useApiError();
  const fetchUrl = useMutation({
    mutationFn: () => mediaApi.get(mediaId),
    onSuccess: (asset) => {
      if (asset.url) window.open(asset.url, '_blank', 'noopener,noreferrer');
      else toast.warning(t('leads.noCvUrl'));
    },
    onError,
  });
  return (
    <div className="rounded-md border-[0.5px] border-hairline bg-canvas-bone p-md">
      <Eyebrow>{t('leads.cv')}</Eyebrow>
      <p className="mt-2xs text-small text-ink-mute">{t('leads.cvRestricted')}</p>
      <div className="mt-sm">
        <Button
          variant="ghost"
          size="sm"
          loading={fetchUrl.isPending}
          onClick={() => fetchUrl.mutate(undefined)}
        >
          {t('leads.openCv')}
        </Button>
      </div>
    </div>
  );
}
